import { createContext, useContext } from 'react';
import { compile } from '@casa/layout';
import type { TileStyle } from '@casa/model';
import { TILE_BACKGROUND, TILE_PADDING, TILE_RADIUS, TITLE_COLOR, TITLE_ROW_HEIGHT } from './theme.ts';

// A tile's style (dashboard-editor spec §5.9, TS-1, TS-2; T-2, T-3): its own, else the
// dashboard's, else the defaults. The text colour reaches the tile's content through a context; a
// tile type's own colours (a value's colour, a switch's on and off) win over it, and those views do
// not ask for it.

/**
 * A style with every property but the text colour, whose default is each tile type's own, the
 * icon's colour, whose default is the title's, and the caption's alignment, the number's by default.
 */
export type ResolvedTileStyle = { readonly [K in Exclude<keyof TileStyle, 'text' | 'iconColor' | 'captionAlign'>]-?: NonNullable<TileStyle[K]> } & Pick<TileStyle, 'text' | 'iconColor' | 'captionAlign'>;

/** Where neither the tile nor the dashboard sets a property (TS-1, the owner's defaults): the legacy tile. */
export const DEFAULT_TILE_STYLE: ResolvedTileStyle = {
    background: TILE_BACKGROUND,
    padding: TILE_PADDING,
    radius: TILE_RADIUS,
    border: 'none',
    borderWidth: 2,
    title: TITLE_COLOR,
    titleHeight: TITLE_ROW_HEIGHT,
    titleBackground: 'none',
    titleUppercase: true,
    // Centred, as the legacy tiles had them (TS-14, TS-15).
    titleAlign: 'center',
    gaugeStyle: 'ticks',
    valueAlign: 'center',
    // A value tile's caption (SR-9, the owner's): 60 % of the title height, grey; lined up as the number (no default).
    captionSize: 60,
    captionColor: '#808080',
    // The tile's icon (TS-10, TS-11, issue #21): at the top right, where it was; 30 pixels the owner's default.
    iconPosition: 'top-right',
    iconSize: 30,
    iconBackground: 'none',
    iconRadius: 0,
    iconMargin: 0,
    // The box shown (DS-8). No shadow; when switched on, the owner's: black at 0x30 of 0xff, straight below, 2 pixels of blur (DS-5, issue #35).
    showBackground: true,
    shadow: false,
    shadowColor: '#30000000',
    shadowX: 0,
    shadowY: 0,
    shadowBlur: 2,
};

/** The tile's style: its own over the dashboard's, property by property, over the defaults. */
export function tileStyleOf(dashboard: TileStyle | undefined, own: TileStyle | undefined) : ResolvedTileStyle {
    const style: Record<string, unknown> = { ...DEFAULT_TILE_STYLE };
    for (const layer of [dashboard, own]) {
        for (const [key, value] of Object.entries(layer ?? {})) {
            if (value !== undefined) {
                style[key] = value;
            }
        }
    }

    return style as ResolvedTileStyle;
}

export const TileTextColorContext = createContext<string | undefined>(undefined);

/** The text colour of the tile being drawn, or `fallback` (the look of before) where neither it nor the dashboard sets one. */
export function useTileTextColor(fallback: string) : string {
    return useContext(TileTextColorContext) ?? fallback;
}

/** A style rule as the value tile has it (dashboard-editor spec §5.10). */
export interface StyleRule {
    readonly when: string;
    readonly style: TileStyle;
    /** The caption while it holds (SR-8). */
    readonly caption?: string | undefined;
}

/** The rules that hold for `value`, top to bottom: none without a value, nor one whose condition cannot be worked out. */
function rulesThatHold(rules: readonly StyleRule[], value: number | null) : StyleRule[] {
    if (value === null) {
        return [];
    }

    return rules.filter((rule) => {
        try {
            const result = compile(rule.when).evaluate({ value });
            return typeof result === 'number' && result !== 0 && !Number.isNaN(result);
        } catch {
            return false;
        }
    });
}

/**
 * The style of the rules that hold for `value` (SR-2): top to bottom, a later one over an earlier
 * one, property by property. Undefined when no rule holds.
 */
export function styleOfRules(rules: readonly StyleRule[], value: number | null) : TileStyle | undefined {
    let style: Record<string, unknown> | undefined;
    for (const rule of rulesThatHold(rules, value)) {
        style = { ...style, ...Object.fromEntries(Object.entries(rule.style).filter(([, each]) => each !== undefined)) };
    }

    return style as TileStyle | undefined;
}

/** The caption of the last rule that holds for `value` and gives one (SR-8); undefined when none does. */
export function captionOfRules(rules: readonly StyleRule[], value: number | null) : string | undefined {
    return rulesThatHold(rules, value).reduce<string | undefined>((caption, rule) => rule.caption ?? caption, undefined);
}

/**
 * Lets a tile's view change its own style while a rule holds (a value tile's style rules): over the
 * tile's own, under nothing. `TileView` provides it; undefined puts the tile's own style back.
 */
export const TileStyleOverrideContext = createContext<(style: TileStyle | undefined) => void>(() => {});

/**
 * The tile's style as its frame draws it, a rule's over the tile's own (`TileView` provides it), for
 * what a tile's content draws by it: the gauge's style (docs/specs/gauge-styles.md).
 */
export const ResolvedTileStyleContext = createContext<ResolvedTileStyle>(DEFAULT_TILE_STYLE);

export function useResolvedTileStyle() : ResolvedTileStyle {
    return useContext(ResolvedTileStyleContext);
}
